import * as THREE from 'three';
import {
  BELFRY, CAPSTAN, DECK, LONGBOAT, MASTS, WHEEL, cannonMount, deckHalfWidth, deckY, halfBreadthXY, mastXAt, sheerY,
} from '../../data/ship';
import type { Battery } from '../../sim/cannons';
import type { DeckCargo } from '../../sim/deck';
import { Batch, box, cachedBatch, lathe, merge, rod, tube, V } from './geom';
import { ladder } from './hullMesh';
import { forInstancing, type ShipMaterials } from '../materials/materials';
import { gameMaterial, primitives, type ArtSet } from './art';

// Deck furniture and ordnance. Static fittings are batched per material;
// moving ones (guns recoiling, the wheel, loose stores, swinging lanterns)
// are instanced or grouped and updated from the sim each frame.

function cannonBarrel(length: number): THREE.BufferGeometry {
  const L = length;
  const b = 0.2 * (L / 2.6);
  // Profile from the cascabel (y=0) to the muzzle (y=L), radius, along +Y.
  const prof: [number, number][] = [
    [0.001, -0.18 * L / 2.6], [0.05 * L / 2.6, -0.17 * L / 2.6], [0.06 * L / 2.6, -0.12 * L / 2.6], [0.035 * L / 2.6, -0.08 * L / 2.6],
    [0.1 * L / 2.6, -0.04 * L / 2.6], [b * 1.12, 0], [b * 1.12, 0.06 * L / 2.6], [b * 1.05, 0.08 * L / 2.6],
    [b * 1.03, 0.36 * L], [b * 1.1, 0.37 * L], [b * 1.1, 0.4 * L], [b * 0.9, 0.41 * L],
    [b * 0.82, 0.62 * L], [b * 0.9, 0.63 * L], [b * 0.9, 0.65 * L], [b * 0.74, 0.66 * L],
    [b * 0.64, 0.93 * L], [b * 0.82, 0.96 * L], [b * 0.84, L], [b * 0.4, L], [b * 0.4, L - 0.05], [0.001, L - 0.05],
  ];
  const g = lathe(prof, 20);
  const tr = new THREE.CylinderGeometry(0.06 * L / 2.6, 0.06 * L / 2.6, b * 3.1, 10);
  tr.rotateZ(Math.PI / 2);
  tr.translate(0, 0.42 * L, 0);
  return merge([g, tr]);
}

function carriage(length: number): { wood: THREE.BufferGeometry; iron: THREE.BufferGeometry } {
  const k = length / 2.6;
  const wood: THREE.BufferGeometry[] = [];
  const iron: THREE.BufferGeometry[] = [];
  // Cheeks: stepped brackets, along local +Y (barrel axis) when laid flat.
  for (const s of [-1, 1]) {
    const shape = [
      new THREE.Vector2(-0.55 * k, 0), new THREE.Vector2(0.55 * k, 0), new THREE.Vector2(0.55 * k, 0.42 * k),
      new THREE.Vector2(0.2 * k, 0.42 * k), new THREE.Vector2(0.2 * k, 0.32 * k), new THREE.Vector2(-0.1 * k, 0.32 * k),
      new THREE.Vector2(-0.1 * k, 0.22 * k), new THREE.Vector2(-0.55 * k, 0.22 * k),
    ];
    const ch = new THREE.ExtrudeGeometry(new THREE.Shape(shape), { depth: 0.1 * k, bevelEnabled: false });
    ch.translate(0, 0, -0.05 * k);
    ch.rotateY(Math.PI / 2);
    ch.translate(s * 0.26 * k, 0, 0);
    wood.push(ch);
  }
  const bed = box(0.5 * k, 0.07 * k, 1.0 * k);
  bed.translate(0, 0.14 * k, 0);
  wood.push(bed);
  for (const z of [-0.42 * k, 0.42 * k]) {
    const ax = box(0.66 * k, 0.12 * k, 0.14 * k);
    ax.translate(0, 0.1 * k, z);
    wood.push(ax);
    for (const s of [-1, 1]) {
      const w = new THREE.CylinderGeometry(0.13 * k, 0.13 * k, 0.07 * k, 14);
      w.rotateZ(Math.PI / 2);
      w.translate(s * 0.37 * k, 0.13 * k, z);
      wood.push(w);
      const hubcap = new THREE.CylinderGeometry(0.035 * k, 0.035 * k, 0.1 * k, 8);
      hubcap.rotateZ(Math.PI / 2);
      hubcap.translate(s * 0.38 * k, 0.13 * k, z);
      iron.push(hubcap);
    }
  }
  // Cap squares over the trunnions.
  for (const s of [-1, 1]) {
    const cs = box(0.12 * k, 0.03 * k, 0.2 * k);
    cs.translate(s * 0.26 * k, 0.43 * k, -0.35 * k);
    iron.push(cs);
  }
  // Origin at the trunnions (front of the carriage faces -z).
  const w = merge(wood).translate(0, 0, 0.35 * k);
  const i = merge(iron).translate(0, 0, 0.35 * k);
  return { wood: w, iron: i };
}

function barrelGeo(): { wood: THREE.BufferGeometry; iron: THREE.BufferGeometry } {
  const prof: [number, number][] = [];
  for (let i = 0; i <= 12; i++) {
    const t = i / 12;
    prof.push([0.3 + 0.06 * Math.sin(t * Math.PI), t * 0.95]);
  }
  prof.unshift([0.001, 0.0]);
  prof.push([0.001, 0.95]);
  const wood = lathe(prof, 18);
  const hoops: THREE.BufferGeometry[] = [];
  for (const t of [0.08, 0.22, 0.78, 0.92]) {
    const r = 0.3 + 0.06 * Math.sin(t * Math.PI) + 0.006;
    const h = new THREE.CylinderGeometry(r, r, 0.035, 18, 1, true);
    h.translate(0, t * 0.95, 0);
    hoops.push(h);
  }
  return { wood, iron: merge(hoops) };
}

function lanternGeo(scale: number): { frame: THREE.BufferGeometry; glass: THREE.BufferGeometry } {
  const s = scale;
  const frame: THREE.BufferGeometry[] = [];
  const base = lathe([[0.001, 0], [0.13 * s, 0], [0.14 * s, 0.03 * s], [0.12 * s, 0.05 * s], [0.001, 0.05 * s]], 12);
  frame.push(base);
  const cap = lathe([[0.001, 0.4 * s], [0.14 * s, 0.4 * s], [0.12 * s, 0.44 * s], [0.06 * s, 0.52 * s], [0.03 * s, 0.6 * s], [0.001, 0.62 * s]], 12);
  frame.push(cap);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    frame.push(rod(V(Math.cos(a) * 0.12 * s, 0.05 * s, Math.sin(a) * 0.12 * s), V(Math.cos(a) * 0.12 * s, 0.4 * s, Math.sin(a) * 0.12 * s), 0.008 * s, 0.008 * s, 4));
  }
  const ring = new THREE.TorusGeometry(0.04 * s, 0.008 * s, 5, 10);
  ring.translate(0, 0.66 * s, 0);
  frame.push(ring);
  const glass = new THREE.CylinderGeometry(0.11 * s, 0.11 * s, 0.35 * s, 12, 1, false);
  glass.translate(0, 0.225 * s, 0);
  return { frame: merge(frame), glass };
}

/** Everything on deck fits in this (design space), for culling the instanced parts. */
const DECK_SPHERE = new THREE.Sphere(new THREE.Vector3(0, 5, 0), 24);

const FITTING_GEO: { barrel?: THREE.BufferGeometry; carriage?: { wood: THREE.BufferGeometry; iron: THREE.BufferGeometry } } = {};

/** One instanced prop: a mesh per material, all placed with the same matrices. */
class Instanced {
  readonly meshes: THREE.InstancedMesh[] = [];

  constructor(parts: { key: string; geometry: THREE.BufferGeometry }[], mats: ShipMaterials, count: number, group: THREE.Group) {
    for (const p of parts) {
      const m = new THREE.InstancedMesh(p.geometry, forInstancing(gameMaterial(mats, p.key, p.geometry)), Math.max(1, count));
      m.castShadow = p.key !== 'lampGlass';
      m.receiveShadow = true;
      m.boundingSphere = DECK_SPHERE.clone();
      m.count = count;
      this.meshes.push(m);
      group.add(m);
    }
  }

  set(i: number, m: THREE.Matrix4): void {
    for (const im of this.meshes) im.setMatrixAt(i, m);
  }

  set count(n: number) {
    // (An empty instanced draw still costs a draw call: none at all instead.)
    for (const im of this.meshes) (im.count = n), (im.visible = n > 0);
  }

  flush(): void {
    for (const im of this.meshes) if (im.count > 0) im.instanceMatrix.needsUpdate = true;
  }
}

export class Fittings {
  readonly group = new THREE.Group();
  private readonly gunBarrels: Instanced;
  private readonly gunCarriages: Instanced;
  private readonly wheel = new THREE.Group();
  private readonly casks: Instanced;
  private readonly crates: Instanced;
  private readonly lanterns: Instanced;
  private readonly artWheel: boolean;
  /** World-space lantern light positions this frame (ship-local). */
  readonly lanternPositions: THREE.Vector3[] = [];

  /**
   * `props`: the Blender-built guns, casks, crates and lanterns (null: the
   * procedural ones). `artStatic`: the deck furniture came with the hull
   * model, so only the moving parts are made here. `wheel`: the Blender wheel.
   */
  constructor(
    private readonly mats: ShipMaterials, private readonly battery: Battery, private readonly cargo: DeckCargo,
    props: ArtSet | null = null, artStatic = false, wheel: { node: THREE.Object3D; position: THREE.Vector3 } | null = null,
  ) {
    if (!artStatic) this.group.add(cachedBatch('fittings', (b) => this.buildStatic(b), mats));
    const prop = (name: string) => {
      const node = props?.node(name);
      return node ? primitives(node) : null;
    };

    // Great guns: instanced barrels and carriages.
    const n = battery.guns.length;
    FITTING_GEO.barrel ??= cannonBarrel(2.6);
    FITTING_GEO.carriage ??= carriage(2.6);
    const car = FITTING_GEO.carriage;
    this.gunBarrels = new Instanced(prop('gun_barrel') ?? [{ key: 'iron', geometry: FITTING_GEO.barrel }], mats, n, this.group);
    this.gunCarriages = new Instanced(prop('gun_carriage') ?? [{ key: 'redWood', geometry: car.wood }, { key: 'iron', geometry: car.iron }], mats, n, this.group);

    // Wheel: turns with the helm.
    this.artWheel = !!wheel;
    if (wheel) {
      this.wheel.position.copy(wheel.position);
      this.wheel.add(wheel.node);
      this.group.add(this.wheel);
    } else this.buildWheel();

    const bg = barrelGeo();
    const nObj = cargo.objects.length;
    this.casks = new Instanced(prop('cask') ?? [{ key: 'timber', geometry: bg.wood }, { key: 'iron', geometry: bg.iron }], mats, nObj, this.group);
    const crateG = merge([box(0.84, 0.7, 0.84)].map((g) => g.translate(0, 0.35, 0)));
    this.crates = new Instanced(prop('crate') ?? [{ key: 'timber', geometry: crateG }], mats, nObj, this.group);

    const lg = lanternGeo(1.0);
    const nl = cargo.lanterns.length;
    this.lanterns = new Instanced(prop('lantern') ?? [{ key: 'brass', geometry: lg.frame }, { key: 'lampGlass', geometry: lg.glass }], mats, nl, this.group);
    for (let i = 0; i < nl; i++) this.lanternPositions.push(new THREE.Vector3());
    this.update(0);
  }

  private buildWheel(): void {
    const g = new Batch();
    const R = 0.72;
    const rim = new THREE.TorusGeometry(R, 0.035, 8, 40);
    g.add('timber', rim);
    const inner = new THREE.TorusGeometry(R * 0.5, 0.03, 8, 32);
    g.add('timber', inner);
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      const d = V(Math.cos(a), Math.sin(a), 0);
      g.add('timber', rod(V(0, 0, 0), d.clone().multiplyScalar(R), 0.025, 0.02, 6));
      const handle = lathe([[0.001, 0], [0.03, 0.02], [0.022, 0.08], [0.03, 0.16], [0.02, 0.2], [0.001, 0.21]], 8);
      handle.rotateZ(-Math.PI / 2);
      handle.rotateZ(a);
      handle.translate(d.x * R, d.y * R, 0);
      g.add('timber', handle);
    }
    const hub = new THREE.CylinderGeometry(0.09, 0.09, 0.16, 14);
    hub.rotateX(Math.PI / 2);
    g.add('brass', hub);
    const spokes = g.build(this.mats);
    this.wheel.add(spokes);
    const y = deckY(WHEEL.x) + 1.05;
    this.wheel.position.set(WHEEL.x, y, 0);
    this.wheel.rotation.y = Math.PI / 2;
    this.group.add(this.wheel);
  }

  private buildStatic(b: Batch): void {
    // Wheel pedestal: barrel between two stands.
    {
      const x = WHEEL.x;
      const y = deckY(x);
      const drum = new THREE.CylinderGeometry(0.2, 0.2, 1.4, 14);
      drum.rotateX(Math.PI / 2);
      drum.translate(x - 0.25, y + 1.05, 0);
      b.add('timber', drum);
      for (const z of [-0.72, 0.72]) {
        const st = box(0.22, 1.2, 0.12);
        st.translate(x - 0.25, y + 0.6, z);
        b.add('blackWood', st);
      }
      // Binnacle with a brass hood.
      const bx = WHEEL.binnacleX;
      const bin = box(0.5, 0.95, 0.5);
      bin.translate(bx, deckY(bx) + 0.48, 0);
      b.add('blackWood', bin);
      const hood = new THREE.SphereGeometry(0.2, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2);
      hood.translate(bx, deckY(bx) + 0.97, 0);
      b.add('brass', hood);
    }
    // Capstan with its bars shipped.
    {
      const x = CAPSTAN.x, y = deckY(x);
      const prof: [number, number][] = [[0.001, 0], [0.72, 0], [0.72, 0.1], [0.5, 0.14], [0.42, 0.25], [0.46, 0.9], [0.62, 0.95], [0.62, 1.2], [0.001, 1.2]];
      const c = lathe(prof, 12);
      c.translate(x, y, 0);
      b.add('timber', c);
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + 0.3;
        b.add('timber', rod(V(x + Math.cos(a) * 0.5, y + 1.08, Math.sin(a) * 0.5), V(x + Math.cos(a) * 2.1, y + 1.02, Math.sin(a) * 2.1), 0.045, 0.04, 6));
      }
    }
    // Belfry with the ship's bell.
    {
      const x = BELFRY.x, y = deckY(DECK.foreBackX + 0.3);
      for (const z of [-0.45, 0.45]) b.add('blackWood', rod(V(x + 0.3, y, z), V(x + 0.3, y + 1.35, z), 0.06, 0.06, 6));
      const beam = box(0.16, 0.14, 1.1);
      beam.translate(x + 0.3, y + 1.38, 0);
      b.add('blackWood', beam);
      const bell = lathe([[0.001, 0.28], [0.06, 0.28], [0.09, 0.2], [0.12, 0.05], [0.17, 0.0], [0.16, -0.01], [0.001, 0.02]], 16);
      bell.translate(x + 0.3, y + 1.02, 0);
      b.add('brass', bell);
    }
    // Bitts forward of the main and fore masts.
    for (const [mx, span] of [[2.2, 1.5], [11.6, 1.3]] as const) {
      const y = deckY(mx);
      for (const z of [-span / 2, span / 2]) {
        const p = box(0.3, 1.1, 0.3);
        p.translate(mx + 0.4, y + 0.55, z);
        b.add('blackWood', p);
      }
      const cb = box(0.2, 0.22, span + 0.5);
      cb.translate(mx + 0.4, y + 0.85, 0);
      b.add('blackWood', cb);
    }
    // Fife rails round the masts and pin rails along the bulwarks.
    const pinGeo = lathe([[0.001, 0], [0.018, 0.0], [0.02, 0.18], [0.03, 0.2], [0.03, 0.24], [0.012, 0.28], [0.001, 0.29]], 6);
    for (const m of MASTS) {
      const y = m.deckY + 0.02;
      const mx = mastXAt(m, y);
      const rr = m.radius + 0.75;
      const rail = new THREE.TorusGeometry(rr, 0.05, 5, 24, Math.PI * 1.2);
      rail.rotateX(Math.PI / 2);
      rail.rotateY(Math.PI * 0.4);
      rail.translate(mx, y + 0.72, 0);
      b.add('blackWood', rail);
      for (let i = 0; i < 5; i++) {
        const a = Math.PI * 0.4 + (i / 4) * Math.PI * 1.2;
        const px = mx + Math.cos(a) * rr, pz = -Math.sin(a) * rr;
        b.add('blackWood', rod(V(px, y, pz), V(px, y + 0.72, pz), 0.04, 0.04, 6));
        for (let k = 0; k < 2; k++) {
          const pa = a + 0.12 + k * 0.1;
          const pin = pinGeo.clone();
          pin.translate(mx + Math.cos(pa) * rr, y + 0.62, -Math.sin(pa) * rr);
          b.add('timber', pin);
        }
      }
      // Pin rails inside the bulwark abreast the mast.
      for (const side of [1, -1]) {
        const px = mx - 0.8;
        const hw = deckHalfWidth(px);
        if (hw < 0) continue;
        const pr = box(2.4, 0.08, 0.16);
        pr.translate(px, deckY(px) + 0.95, side * (hw - 0.08));
        b.add('timber', pr);
        for (let k = 0; k < 8; k++) {
          const pin = pinGeo.clone();
          pin.translate(px - 1.05 + k * 0.3, deckY(px) + 0.85, side * (hw - 0.08));
          b.add('timber', pin);
        }
        // A coil of rope hanging from the pins.
        const coil: THREE.Vector3[] = [];
        for (let a = 0; a < Math.PI * 2 * 4; a += 0.3) {
          const rad = 0.2 + a * 0.002;
          coil.push(V(px + Math.cos(a) * 0.05, deckY(px) + 0.55 + Math.sin(a) * rad * 0.9, side * (hw - 0.14) + Math.sin(a * 0.5) * 0.01));
        }
        b.add('rope', tube(coil, 0.018, 5, 12));
      }
    }
    // Quarterdeck balustrade and forecastle rail with turned balusters.
    const baluster = lathe([[0.001, 0], [0.05, 0], [0.05, 0.06], [0.03, 0.1], [0.055, 0.3], [0.03, 0.52], [0.045, 0.56], [0.045, 0.62], [0.001, 0.62]], 8);
    for (const [x, y0] of [[DECK.quarterFrontX + 0.12, DECK.quarterY + 0.1], [DECK.foreBackX - 0.1, DECK.foreY + 0.1]] as const) {
      const hw = deckHalfWidth(x) - 0.05;
      const stairGap = x < 0 ? 2.6 : 1.8;
      for (let z = -hw + 0.1; z <= hw - 0.1; z += 0.22) {
        if (Math.abs(Math.abs(z) - stairGap) < 0.5) continue;
        const bb = baluster.clone();
        bb.translate(x, y0 + 0.1, z);
        b.add('timber', bb);
      }
      const top = box(0.14, 0.08, hw * 2);
      top.translate(x, y0 + 0.76, 0);
      b.add('blackWood', top);
      const bottom = box(0.14, 0.1, hw * 2);
      bottom.translate(x, y0 + 0.1, 0);
      b.add('blackWood', bottom);
    }
    // Stairs up to the quarterdeck and forecastle.
    for (const s of [-1, 1]) {
      ladder(b, DECK.quarterFrontX + 1.7, DECK.mainY + 0.1, DECK.quarterFrontX + 0.15, DECK.quarterY + 0.12, s * 2.6, 0.7);
      ladder(b, DECK.foreBackX - 1.2, deckY(11) + 0.1, DECK.foreBackX - 0.12, DECK.foreY + 0.12, s * 1.8, 0.6);
    }
    // Chain pumps abaft the mainmast.
    for (const z of [-0.7, 0.7]) {
      const p = box(0.4, 0.8, 0.4);
      p.translate(-1.2, deckY(-1.2) + 0.4, z);
      b.add('timber', p);
      b.add('iron', rod(V(-1.2, deckY(-1.2) + 0.8, z), V(-1.2, deckY(-1.2) + 0.95, z * 2.6), 0.02, 0.02, 6));
    }
    // The longboat on the skid beams.
    this.buildBoat(b);
    // Catheads and anchors.
    for (const side of [1, -1]) {
      const x = 16.6, y = sheerY(16.6) - 0.15;
      const hb = halfBreadthXY(x, y);
      const cat = box(1.7, 0.32, 0.34);
      cat.rotateY(-side * 0.45);
      cat.translate(x + 0.2, y, side * (hb + 0.35));
      b.add('blackWood', cat);
      const end = V(x + 0.55, y - 0.1, side * (hb + 1.05));
      this.buildAnchor(b, end, side);
    }
    // Hawse holes and anchor cable run in from the bow.
    for (const side of [1, -1]) {
      const hx = 17.6, hy = 1.9;
      const hbh = halfBreadthXY(hx, hy);
      const hole = new THREE.TorusGeometry(0.2, 0.06, 8, 16);
      hole.rotateY(Math.PI / 2 - side * 0.8);
      hole.translate(hx, hy, side * (hbh + 0.02));
      b.add('iron', hole);
    }
    // Dark hold under the main deck so nothing shows through gratings.
    const hold = box(30, 0.2, 9);
    hold.translate(1.5, DECK.mainY - 1.6, 0);
    b.add('dark', hold);
    // Stern lantern brackets.
    for (const l of this.cargo.lanterns) {
      if (l.hanging) continue;
      b.add('iron', rod(V(l.pivot.x + 0.35, l.pivot.y - 1.1, l.pivot.z * 0.95), V(l.pivot.x, l.pivot.y - 0.05, l.pivot.z), 0.035, 0.03, 6));
    }
  }

  private buildBoat(b: Batch): void {
    const L = LONGBOAT.length, B = LONGBOAT.beam;
    const pos: number[] = [];
    const uv: number[] = [];
    const idx: number[] = [];
    const NU = 24, NV = 10;
    for (let j = 0; j <= NV; j++) {
      const t = j / NV;
      for (let i = 0; i <= NU; i++) {
        const u = i / NU;
        const x = (u - 0.5) * L;
        const e = Math.pow(Math.abs(u - 0.5) * 2, 2.2);
        const hb = (B / 2) * Math.sqrt(Math.max(0, 1 - e)) * Math.sin((t * Math.PI) / 2 + 0.25);
        const y = -0.62 * Math.cos((t * Math.PI) / 2) + 0.12 * e;
        pos.push(x, y, hb);
        uv.push(x, t);
      }
    }
    for (let j = 0; j < NV; j++) for (let i = 0; i < NU; i++) {
      const a = j * (NU + 1) + i, c = a + NU + 1;
      idx.push(a, a + 1, c, a + 1, c + 1, c);
    }
    const half = new THREE.BufferGeometry();
    half.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    half.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    half.setIndex(idx);
    half.computeVertexNormals();
    const other = half.clone();
    other.scale(1, 1, -1);
    const oi = other.getIndex()!;
    for (let i = 0; i < oi.count; i += 3) {
      const t = oi.getX(i + 1);
      oi.setX(i + 1, oi.getX(i + 2));
      oi.setX(i + 2, t);
    }
    other.computeVertexNormals();
    const inner = merge([half.clone(), other.clone()]);
    const boat = merge([half, other]);
    const m = new THREE.Matrix4().makeTranslation(LONGBOAT.x, LONGBOAT.y + 0.62, 0);
    b.add('hull', boat, m);
    // Inside surface (double-sided look) slightly smaller.
    inner.scale(0.97, 0.97, 0.94);
    const ii = inner.getIndex();
    if (ii) for (let i = 0; i < ii.count; i += 3) {
      const t = ii.getX(i + 1);
      ii.setX(i + 1, ii.getX(i + 2));
      ii.setX(i + 2, t);
    }
    inner.computeVertexNormals();
    b.add('timber', inner, new THREE.Matrix4().makeTranslation(LONGBOAT.x, LONGBOAT.y + 0.64, 0));
    for (let i = -2; i <= 2; i++) {
      const th = box(0.2, 0.05, B * 0.82);
      th.translate(LONGBOAT.x + i * 1.0, LONGBOAT.y + 0.4, 0);
      b.add('timber', th);
    }
    // Oars stowed across the thwarts.
    for (const z of [-0.3, -0.1, 0.1, 0.3]) b.add('spar', rod(V(LONGBOAT.x - 2.6, LONGBOAT.y + 0.47, z), V(LONGBOAT.x + 2.4, LONGBOAT.y + 0.47, z * 1.1), 0.028, 0.02, 6));
    // Skid beams.
    for (const x of [LONGBOAT.x - 1.8, LONGBOAT.x + 1.8]) {
      const s = box(0.22, 0.2, deckHalfWidth(x) * 2 - 0.2);
      s.translate(x, LONGBOAT.y - 0.3, 0);
      b.add('blackWood', s);
      for (const z of [-2.6, 2.6]) {
        const post = box(0.2, LONGBOAT.y - 0.3 - deckY(x), 0.2);
        post.translate(x, (LONGBOAT.y - 0.3 + deckY(x)) / 2, z);
        b.add('blackWood', post);
      }
    }
  }

  private buildAnchor(b: Batch, top: THREE.Vector3, side: number): void {
    const shankLen = 3.2;
    const bottom = V(top.x - 0.2, top.y - shankLen, top.z + side * 0.25);
    b.add('iron', rod(bottom, top, 0.08, 0.06, 8));
    // Arms and flukes.
    const dir = new THREE.Vector3().subVectors(top, bottom).normalize();
    const across = V(1, 0, 0).addScaledVector(dir, -dir.x).normalize();
    for (const s of [-1, 1]) {
      const pts = [bottom.clone(), bottom.clone().addScaledVector(across, s * 0.6).addScaledVector(dir, 0.2), bottom.clone().addScaledVector(across, s * 0.95).addScaledVector(dir, 0.75)];
      b.add('iron', tube(pts, 0.065, 7, 12));
      const tip = pts[2]!;
      const fl = box(0.08, 0.45, 0.32);
      fl.translate(tip.x, tip.y - 0.05, tip.z);
      b.add('iron', fl);
    }
    // Wooden stock near the ring.
    const stock = box(0.2, 0.2, 2.6);
    stock.rotateY(0.3 * side);
    stock.translate(top.x, top.y - 0.35, top.z);
    b.add('timber', stock);
    const ring = new THREE.TorusGeometry(0.2, 0.04, 8, 16);
    ring.translate(top.x, top.y + 0.15, top.z);
    b.add('iron', ring);
    // Cathead stopper.
    b.add('rope', rod(V(top.x, top.y + 0.35, top.z), V(top.x - 0.1, top.y + 0.08, top.z), 0.03));
  }

  /** Per-frame: gun recoil, wheel, loose stores and lanterns. */
  update(helm: number): void {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const e = new THREE.Euler();
    const p = new THREE.Vector3();
    const sc = new THREE.Vector3();
    const trunnion = new THREE.Vector3(), breech = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
    this.battery.guns.forEach((c, i) => {
      const mount = cannonMount(c.spec);
      const k = c.spec.length / 2.6;
      const side = c.spec.side;
      const deck = deckY(mount.x) + 0.02;
      if (c.destroyed) {
        // Dismounted: the carriage smashed over on its side, the barrel
        // lying across the deck where it fell.
        e.set(Math.PI / 2 * side + side * 0.25, 0.9 + i * 0.37, 0.12);
        q.setFromEuler(e);
        p.set(mount.x + 0.3, deck + 0.18 * k, mount.z - side * 1.6);
        m.compose(p, q, sc.set(k, k, k));
        this.gunBarrels.set(i, m);
        e.set(side * 1.2, side > 0 ? Math.PI + 0.4 : 0.4, 0.3);
        q.setFromEuler(e);
        p.set(mount.x - 0.2, deck + 0.3 * k, mount.z - side * 0.6);
        m.compose(p, q, sc.set(k, k, k));
        this.gunCarriages.set(i, m);
        return;
      }
      // Run in by the recoil; trained with handspikes, the whole gun swings
      // about her port (the breech levered round, the muzzle kept in it).
      const zb = mount.z - side * c.recoil * 1.05 * k;
      const port = mount.z + side * c.spec.length * 0.42;
      const ca = Math.cos(side * c.train), sa = Math.sin(side * c.train);
      const swing = (y: number, z: number): THREE.Vector3 => p.set(mount.x + sa * (z - port), y, port + ca * (z - port));
      // Barrel axis outboard (+z for starboard), raised on her trunnions by the quoin.
      e.set(Math.PI / 2 * side - side * c.elevation, side * c.train, 0, 'YXZ');
      q.setFromEuler(e);
      trunnion.copy(swing(deck + 0.44 * k, zb));
      p.copy(trunnion).sub(breech.set(0, c.spec.length * 0.42, 0).applyQuaternion(q));
      sc.set(k, k, k);
      m.compose(p, q, sc);
      this.gunBarrels.set(i, m);
      // Carriage on the deck under the trunnions, front to the port.
      q.setFromAxisAngle(up, (side > 0 ? Math.PI : 0) + side * c.train);
      m.compose(swing(deck, zb), q, sc.set(k, k, k));
      this.gunCarriages.set(i, m);
    });
    this.gunBarrels.flush();
    this.gunCarriages.flush();

    // The procedural wheel turns about its local z (it's set side-on); the model's about +X.
    if (this.artWheel) this.wheel.rotation.x = helm * Math.PI * 1.6;
    else this.wheel.rotation.z = helm * Math.PI * 1.6;

    let nb = 0, nc = 0;
    for (const o of this.cargo.objects) {
      e.set(o.tiltX, o.yaw, o.tiltZ);
      q.setFromEuler(e);
      p.set(o.x, deckY(o.x) + 0.13, o.z);
      m.compose(p, q, sc.set(1, 1, 1));
      if (o.kind === 'barrel') this.casks.set(nb++, m);
      else this.crates.set(nc++, m);
    }
    this.casks.count = nb;
    this.crates.count = nc;
    this.casks.flush();
    this.crates.flush();

    this.cargo.lanterns.forEach((l, i) => {
      const dir = new THREE.Vector3(l.pivot.x - l.bob.x, l.pivot.y - l.bob.y, l.pivot.z - l.bob.z);
      if (l.hanging && dir.lengthSq() > 1e-6) q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
      else q.identity();
      const s = l.hanging ? 0.9 : 1.6;
      p.set(l.bob.x, l.bob.y - (l.hanging ? 0.62 * s : 0), l.bob.z);
      if (l.hanging) p.set(l.bob.x, l.bob.y, l.bob.z).addScaledVector(new THREE.Vector3(0, -1, 0).applyQuaternion(q), 0.62 * s);
      m.compose(p, q, sc.set(s, s, s));
      this.lanterns.set(i, m);
      this.lanternPositions[i]!.copy(p).addScaledVector(new THREE.Vector3(0, 1, 0).applyQuaternion(q), 0.25 * s);
    });
    this.lanterns.flush();
  }
}
